import React, { useEffect, useState, useMemo } from "react";
import { useLang, tr } from "../contexts/LangContext";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Wallet, Receipt, Warehouse, Plus, Trash2, X, Check, Download } from "lucide-react";
import StageRegistrationsPanel from "../components/StageRegistrationsPanel";
import OrdersPanel from "../components/OrdersPanel";

export default function Treasury() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [tab, setTab] = useState("summary");
  const [year, setYear] = useState(new Date().getFullYear());
  const [fees, setFees] = useState([]);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [members, setMembers] = useState([]);
  const [garages, setGarages] = useState([]);

  const load = async () => {
    try {
      const [f, p, s, m, g] = await Promise.all([
        api.get("/fee-schedule"),
        api.get("/treasury/payments", { params: { year } }),
        api.get("/treasury/summary", { params: { year } }),
        api.get("/treasury/members"),
        api.get("/treasury/garages"),
      ]);
      setFees(f.data); setPayments(p.data); setSummary(s.data); setMembers(m.data); setGarages(g.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, [year]);

  if (!user || !(["admin", "treasurer"].includes(user.role) || (user.roles || []).some((r) => ["admin", "treasurer"].includes(r)))) return null;

  return (
    <div className="pt-24 md:pt-28 pb-16 md:pb-24 min-h-screen bg-sand" data-testid="treasury-page">
      <div className="max-w-7xl mx-auto px-4 md:px-10 lg:px-12">
        <div className="label-eyebrow text-club-red mb-3">{t.treasury.title}</div>
        <h1 className="font-serif text-4xl text-navy">{tr("Trésorerie")}</h1>
        <div className="divider-gold mt-5 mb-8" />

        <div className="flex items-center gap-3 mb-6">
          <span className="text-xs uppercase tracking-widest text-slate-500">{t.common.year}</span>
          <input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value) || new Date().getFullYear())} className="w-32 border border-slate-300 px-3 py-2" data-testid="treasury-year" />
        </div>

        <div className="flex flex-nowrap gap-1 border-b border-slate-200 mb-8 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 scrollbar-thin" data-testid="treasury-tabs">
          {["summary", "reports", "ledger", "income", "expenses", "payments", "garages", "fees", "members", "stages", "orders"].map((k) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-4 py-3 text-xs uppercase tracking-[0.2em] border-b-2 whitespace-nowrap shrink-0 ${tab === k ? "border-club-red text-club-red font-semibold" : "border-transparent text-slate-500 hover:text-navy"}`}
              data-testid={`treasury-tab-${k}`}>
              {t.treasury.tabs[k] || (k === "members" ? tr("Membres") : k === "reports" ? tr("Rapports") : k === "income" ? tr("Recettes") : k === "expenses" ? tr("Dépenses") : k === "ledger" ? tr("Grand Livre") : k === "stages" ? tr("Stages") : k === "orders" ? tr("Commandes") : k)}
            </button>
          ))}
        </div>

        {tab === "summary" && <SummaryView summary={summary} t={t} />}
        {tab === "reports" && <ReportsView />}
        {tab === "ledger" && <LedgerView year={year} />}
        {tab === "income" && <EntriesView entryType="income" year={year} />}
        {tab === "expenses" && <EntriesView entryType="expense" year={year} />}
        {tab === "payments" && <PaymentsView payments={payments} fees={fees} members={members} year={year} reload={load} t={t} lang={lang} />}
        {tab === "garages" && <GaragesView garages={garages} fees={fees} members={members} reload={load} t={t} />}
        {tab === "fees" && <FeesView fees={fees} t={t} />}
        {tab === "members" && <MembersFullView />}
        {tab === "stages" && <StageRegistrationsPanel embedded />}
        {tab === "orders" && <OrdersPanel embedded />}
      </div>
    </div>
  );
}

function SummaryView({ summary, t }) {
  if (!summary) return <div className="text-slate-500">{tr("Chargement…")}</div>;
  return (
    <div data-testid="treasury-summary">
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        <div className="bg-club-red text-white p-6">
          <Wallet className="w-7 h-7 mb-3 text-club-yellow" strokeWidth={1.25} />
          <div className="font-serif text-4xl">€ {summary.grand_total.toFixed(0)}</div>
          <div className="text-[10px] uppercase tracking-widest mt-1 text-club-yellow">Total {summary.year || ""}</div>
        </div>
        {summary.by_category.map((c) => (
          <div key={c.category} className="bg-white border border-slate-200 p-6" data-testid={`summary-${c.category}`}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-club-red font-semibold">{t.treasury.cats[c.category] || c.category}</div>
            <div className="font-serif text-3xl text-navy mt-3">€ {c.total.toFixed(0)}</div>
            <div className="text-xs text-slate-500 mt-1">{c.count} {tr("paiements")}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PaymentsView({ payments, fees, members, year, reload, t, lang }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ member_id: "", item_key: "", amount: "", year, notes: "" });
  const feesByKey = useMemo(() => Object.fromEntries(fees.map((f) => [f.key, f])), [fees]);

  const submit = async (e) => {
    e.preventDefault();
    const fee = feesByKey[form.item_key];
    if (!fee) return;
    await api.post("/treasury/payments", {
      member_id: form.member_id, category: fee.category, item_key: form.item_key,
      amount: parseFloat(form.amount) || fee.amount, year: parseInt(form.year), notes: form.notes,
    });
    setShowForm(false);
    setForm({ member_id: "", item_key: "", amount: "", year, notes: "" });
    reload();
  };

  const del = async (id) => {
    if (!window.confirm(tr("Supprimer ce paiement ?"))) return;
    await api.delete(`/treasury/payments/${id}`);
    reload();
  };

  const memberById = (id) => members.find((m) => m.id === id);

  return (
    <div data-testid="treasury-payments">
      <button onClick={() => setShowForm(!showForm)} className="mb-6 inline-flex items-center gap-2 bg-navy text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-club-red" data-testid="payment-toggle">
        <Plus className="w-4 h-4" /> {t.treasury.recordPayment}
      </button>

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-slate-200 p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="payment-form">
          <select required value={form.member_id} onChange={(e) => setForm({ ...form, member_id: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="payment-member">
            <option value="">{t.treasury.selectMember}</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name} — {m.email}</option>)}
          </select>
          <select required value={form.item_key} onChange={(e) => {
            const fee = feesByKey[e.target.value];
            setForm({ ...form, item_key: e.target.value, amount: fee ? fee.amount : "" });
          }} className="border border-slate-300 px-3 py-2" data-testid="payment-item">
            <option value="">{t.treasury.selectItem}</option>
            {fees.map((f) => <option key={f.key} value={f.key}>{f[`label_${lang}`] || f.label_fr} — € {f.amount.toFixed(0)}</option>)}
          </select>
          <input required type="number" step="0.01" placeholder={tr("Montant")} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="payment-amount" />
          <input required type="number" placeholder={tr("Année")} value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="payment-year" />
          <input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border border-slate-300 px-3 py-2 md:col-span-2" data-testid="payment-notes" />
          <button type="submit" className="md:col-span-2 bg-club-red text-white py-3 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark" data-testid="payment-submit">{t.common.confirm}</button>
        </form>
      )}

      {payments.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 p-12 text-center text-slate-500">—</div>
      ) : (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy text-white text-left">
              <tr>
                {["Membre", "Catégorie", "Article", "Montant", "Année", "Date", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-xs uppercase tracking-widest font-medium">{tr(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => {
                const m = memberById(p.member_id);
                return (
                  <tr key={p.id} className="border-t border-slate-100" data-testid={`payment-row-${p.id}`}>
                    <td className="px-4 py-3 text-navy">{m ? m.name : "—"}</td>
                    <td className="px-4 py-3 text-xs uppercase tracking-widest">{t.treasury.cats[p.category] || p.category}</td>
                    <td className="px-4 py-3 text-slate-600">{feesByKey[p.item_key]?.[`label_${lang}`] || p.item_key}</td>
                    <td className="px-4 py-3 font-semibold text-navy">€ {p.amount.toFixed(2)}</td>
                    <td className="px-4 py-3">{p.year}</td>
                    <td className="px-4 py-3 text-slate-500">{new Date(p.created_at).toLocaleDateString(lang === "en" ? "en-GB" : lang)}</td>
                    <td className="px-4 py-3 text-right"><button onClick={() => del(p.id)} className="text-red-600 hover:text-red-800" data-testid={`payment-del-${p.id}`}><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function GaragesView({ garages, fees, members, reload, t }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ member_id: "", garage_type: "garage_bois", identifier: "", notes: "" });

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/treasury/garages", form);
    setShowForm(false);
    setForm({ member_id: "", garage_type: "garage_bois", identifier: "", notes: "" });
    reload();
  };

  const del = async (id) => {
    if (!window.confirm(tr("Libérer cet emplacement ?"))) return;
    await api.delete(`/treasury/garages/${id}`);
    reload();
  };

  const garageTypes = fees.filter((f) => ["garage", "parking"].includes(f.category));

  return (
    <div data-testid="treasury-garages">
      <button onClick={() => setShowForm(!showForm)} className="mb-6 inline-flex items-center gap-2 bg-navy text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-club-red" data-testid="garage-toggle">
        <Plus className="w-4 h-4" /> {t.treasury.assignGarage}
      </button>

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-slate-200 p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="garage-form">
          <select required value={form.member_id} onChange={(e) => setForm({ ...form, member_id: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="garage-member">
            <option value="">{t.treasury.selectMember}</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name} — {m.email}</option>)}
          </select>
          <select required value={form.garage_type} onChange={(e) => setForm({ ...form, garage_type: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="garage-type">
            {garageTypes.map((g) => <option key={g.key} value={g.key}>{g.label_fr} — € {g.amount.toFixed(0)}</option>)}
          </select>
          <input placeholder="N° d'emplacement (ex: G12)" value={form.identifier} onChange={(e) => setForm({ ...form, identifier: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="garage-id" />
          <input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="garage-notes" />
          <button type="submit" className="md:col-span-2 bg-club-red text-white py-3 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark" data-testid="garage-submit">{t.common.confirm}</button>
        </form>
      )}

      {garages.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 p-12 text-center text-slate-500">—</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {garages.map((g) => (
            <div key={g.id} className="bg-white border border-slate-200 p-5" data-testid={`garage-${g.id}`}>
              <Warehouse className="w-6 h-6 text-club-red mb-3" strokeWidth={1.5} />
              <div className="font-serif text-xl text-navy">{g.identifier || "—"}</div>
              <div className="text-xs uppercase tracking-widest text-club-yellow-dark mt-1">{g.garage_type}</div>
              <div className="text-sm text-slate-600 mt-3">{g.member?.name}</div>
              <div className="text-xs text-slate-500">{g.member?.email}</div>
              <button onClick={() => del(g.id)} className="mt-3 text-[10px] uppercase tracking-widest text-red-600 hover:underline" data-testid={`garage-del-${g.id}`}>{tr("Libérer")}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FeesView({ fees, t }) {
  const byCat = useMemo(() => {
    const acc = {};
    fees.forEach((f) => { (acc[f.category] = acc[f.category] || []).push(f); });
    return acc;
  }, [fees]);

  return (
    <div className="space-y-8" data-testid="treasury-fees">
      {Object.entries(byCat).map(([cat, items]) => (
        <div key={cat}>
          <h3 className="font-serif text-2xl text-navy mb-3">{t.treasury.cats[cat] || cat}</h3>
          <div className="divider-gold mb-4" />
          <div className="bg-white border border-slate-200 divide-y divide-slate-100">
            {items.map((f) => (
              <div key={f.key} className="flex items-center justify-between p-4" data-testid={`fee-${f.key}`}>
                <div className="text-navy">{f.label_fr}</div>
                <div className="font-serif text-xl text-club-red">€ {f.amount.toFixed(0)}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- MEMBERS FULL VIEW (alphabetical, editable) ---------- */
function MembersFullView() {
  const [members, setMembers] = useState([]);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null); // member object
  const [form, setForm] = useState({ first_name: "", last_name: "", address: "", phone: "", email: "" });
  const [err, setErr] = useState("");
  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState({ first_name: "", last_name: "", email: "", address: "", phone: "", password: "" });
  const [createInfo, setCreateInfo] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/treasury/members-full"); setMembers(data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return members;
    return members.filter((m) => (
      (m.name || "").toLowerCase().includes(q) ||
      (m.email || "").toLowerCase().includes(q) ||
      (m.first_name || "").toLowerCase().includes(q) ||
      (m.last_name || "").toLowerCase().includes(q) ||
      (m.address || "").toLowerCase().includes(q) ||
      (m.phone || "").toLowerCase().includes(q)
    ));
  }, [members, query]);

  const startEdit = (m) => {
    setEditing(m);
    setForm({
      first_name: m.first_name || (m.name ? m.name.split(" ")[0] : "") || "",
      last_name:  m.last_name  || (m.name ? m.name.split(" ").slice(1).join(" ") : "") || "",
      address:    m.address || "",
      phone:      m.phone   || "",
      email:      m.email   || "",
    });
    setErr("");
  };

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.patch(`/treasury/members/${editing.id}`, form);
      setEditing(null);
      load();
    } catch (e) { setErr(e.response?.data?.detail || "Erreur"); }
  };

  const submitCreate = async (e) => {
    e.preventDefault(); setErr("");
    try {
      const { data } = await api.post("/treasury/members", newForm);
      const pw = data.generated_password || newForm.password;
      setCreateInfo(`Compte créé pour ${data.email}. Mot de passe : ${pw}`);
      setNewForm({ first_name: "", last_name: "", email: "", address: "", phone: "", password: "" });
      setCreating(false);
      load();
      setTimeout(() => setCreateInfo(""), 12000);
    } catch (e) { setErr(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div data-testid="treasury-members-full">
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <h3 className="font-serif text-2xl text-navy">{tr("Membres")} ({filtered.length})</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            placeholder={tr("Rechercher nom, email, téléphone…")}
            value={query} onChange={(e) => setQuery(e.target.value)}
            className="border border-slate-300 px-3 py-2 w-72"
            data-testid="members-search"
          />
          <button onClick={() => setCreating(!creating)} className="inline-flex items-center gap-2 bg-club-red text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark" data-testid="members-add-toggle">
            <Plus className="w-4 h-4" /> {tr("Ajouter membre")}
          </button>
        </div>
      </div>

      {createInfo && (
        <div className="bg-green-50 border border-green-300 text-green-900 p-4 mb-4 text-sm flex items-start gap-2" data-testid="members-create-info">
          <Check className="w-4 h-4 mt-0.5 shrink-0" /> {createInfo}
        </div>
      )}

      {creating && (
        <form onSubmit={submitCreate} className="bg-white border border-slate-200 p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="members-create-form">
          <input required placeholder={tr("Prénom")} value={newForm.first_name} onChange={(e) => setNewForm({ ...newForm, first_name: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="members-create-first-name" />
          <input required placeholder={tr("Nom")} value={newForm.last_name} onChange={(e) => setNewForm({ ...newForm, last_name: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="members-create-last-name" />
          <input required type="email" placeholder={tr("Email")} value={newForm.email} onChange={(e) => setNewForm({ ...newForm, email: e.target.value })} className="border border-slate-300 px-3 py-2 md:col-span-2" data-testid="members-create-email" />
          <input placeholder={tr("Adresse")} value={newForm.address} onChange={(e) => setNewForm({ ...newForm, address: e.target.value })} className="border border-slate-300 px-3 py-2 md:col-span-2" data-testid="members-create-address" />
          <input placeholder={tr("Téléphone")} value={newForm.phone} onChange={(e) => setNewForm({ ...newForm, phone: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="members-create-phone" />
          <input placeholder={tr("Mot de passe (auto si vide)")} value={newForm.password} onChange={(e) => setNewForm({ ...newForm, password: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="members-create-password" />
          {err && <div className="md:col-span-2 text-red-600 text-sm" data-testid="members-create-err">{err}</div>}
          <div className="md:col-span-2 flex gap-3">
            <button type="submit" className="flex-1 bg-club-red text-white py-3 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark" data-testid="members-create-submit">{tr("Créer le membre")}</button>
            <button type="button" onClick={() => setCreating(false)} className="px-5 bg-slate-200 text-navy py-3 text-xs uppercase tracking-[0.2em]" data-testid="members-create-cancel">{tr("Annuler")}</button>
          </div>
        </form>
      )}

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-navy text-white text-left">
            <tr>{["Prénom", "Nom", "Adresse", "Téléphone", "Email", "Rôle", ""].map((h) => (
              <th key={h} className="px-4 py-3 text-xs uppercase tracking-widest font-medium">{tr(h)}</th>
            ))}</tr>
          </thead>
          <tbody>
            {filtered.map((m) => {
              const fn = m.first_name || (m.name ? m.name.split(" ")[0] : "") || "";
              const ln = m.last_name  || (m.name ? m.name.split(" ").slice(1).join(" ") : "") || "";
              return (
                <tr key={m.id} className="border-t border-slate-100" data-testid={`member-row-${m.id}`}>
                  <td className="px-4 py-3 text-navy">{fn || "—"}</td>
                  <td className="px-4 py-3 font-medium text-navy">{ln || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{m.address || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{m.phone || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{m.email}</td>
                  <td className="px-4 py-3"><span className="text-[10px] uppercase tracking-widest px-2 py-1 bg-slate-100 text-slate-700">{m.role}</span></td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => startEdit(m)} className="text-xs uppercase tracking-widest text-club-red hover:underline" data-testid={`member-edit-${m.id}`}>{tr("Modifier")}</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="p-8 text-center text-slate-500">{tr("Aucun membre.")}</div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[60] bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="member-edit-modal" onClick={() => setEditing(null)}>
          <form onSubmit={save} className="bg-white max-w-xl w-full p-8 relative my-12 grid grid-cols-1 md:grid-cols-2 gap-4" onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => setEditing(null)} className="absolute top-4 right-4 text-slate-500 hover:text-navy" data-testid="member-edit-close"><X className="w-5 h-5" /></button>
            <div className="md:col-span-2">
              <div className="label-eyebrow text-club-red mb-2">{tr("Fiche membre")}</div>
              <h2 className="font-serif text-2xl text-navy">{editing.name || editing.email}</h2>
              <div className="divider-gold mt-4 mb-2" />
            </div>
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">{tr("Prénom")}</div>
              <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="member-edit-first-name" />
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">{tr("Nom")}</div>
              <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="member-edit-last-name" />
            </label>
            <label className="block md:col-span-2">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">{tr("Adresse")}</div>
              <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="member-edit-address" />
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">{tr("Téléphone")}</div>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="member-edit-phone" />
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">{tr("Email")}</div>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid="member-edit-email" />
            </label>
            {err && <div className="md:col-span-2 text-red-600 text-sm" data-testid="member-edit-err">{err}</div>}
            <div className="md:col-span-2 flex gap-3 mt-3">
              <button type="submit" className="flex-1 bg-club-red text-white py-3 text-xs uppercase tracking-[0.25em] hover:bg-club-red-dark" data-testid="member-edit-save">{tr("Enregistrer")}</button>
              <button type="button" onClick={() => setEditing(null)} className="px-5 bg-slate-200 text-navy py-3 text-xs uppercase tracking-[0.25em] hover:bg-slate-300" data-testid="member-edit-cancel">{tr("Annuler")}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

/* ---------- TREASURY REPORTS ---------- */
function ReportsView() {
  const [report, setReport] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [trigStatus, setTrigStatus] = useState("");
  const [triggering, setTriggering] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/treasury/reports", { params: { year } }); setReport(data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, [year]);

  const triggerDues = async () => {
    if (!window.confirm(`Envoyer un rappel cotisation ${year} à tous les membres non en règle ?`)) return;
    setTriggering(true); setTrigStatus("");
    try {
      const fd = new FormData(); fd.append("year", year);
      const { data } = await api.post("/admin/notifications/trigger/dues-reminder", fd);
      setTrigStatus(`${tr("Rappel envoyé à")} ${data.sent || 0} ${tr("membre(s) en attente de paiement.")}`);
    } catch (e) {
      setTrigStatus(e.response?.data?.detail || "Erreur");
    } finally { setTriggering(false); }
  };

  if (!report) return <div className="text-slate-500">{tr("Chargement…")}</div>;
  const MONTHS_LBL = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
  const maxMonth = Math.max(1, ...Object.values(report.by_month));

  return (
    <div data-testid="treasury-reports">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h3 className="font-serif text-2xl text-navy">{tr("Rapports financiers")} · {report.year}</h3>
        <div className="flex flex-wrap gap-2 items-center">
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="border border-slate-300 px-3 py-2 text-sm" data-testid="report-year">
            {[2024, 2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/treasury/annual-report.pdf?year=${year}`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-club-red text-white px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-navy"
            data-testid="export-annual-pdf"
          >
            <Download className="w-3.5 h-3.5" />{tr("Rapport annuel PDF (AG)")}</a>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/treasury/reports-export.xlsx?year=${year}`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-emerald-700 text-white px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-emerald-800"
            data-testid="export-xlsx-report"
          >
            <Download className="w-3.5 h-3.5" />{tr("Rapport XLSX")}</a>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/treasury/members-export.xlsx`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-emerald-700 text-white px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-emerald-800"
            data-testid="export-xlsx-members"
          >
            <Download className="w-3.5 h-3.5" />{tr("Membres XLSX")}</a>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/treasury/members-export.csv`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-navy text-white px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-club-red"
            data-testid="export-csv"
          >
            <Download className="w-3.5 h-3.5" />{tr("Export CSV membres")}</a>
          <button onClick={triggerDues} disabled={triggering} className="inline-flex items-center gap-2 bg-club-red text-white px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark disabled:opacity-60" data-testid="trigger-dues">{tr("Rappel cotisation")}</button>
        </div>
      </div>
      {trigStatus && (
        <div className="bg-green-50 border border-green-300 text-green-900 p-3 text-sm mb-6" data-testid="trigger-status">
          {trigStatus}
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
        <KPI label={tr("Revenu total")} value={`€ ${report.revenue.grand_total.toLocaleString("fr")}`} accent="bg-navy text-white" />
        <KPI label={tr("Paiements")} value={`€ ${report.revenue.payments_total.toLocaleString("fr")}`} sub={`${report.revenue.payments_count} ${tr("entrées")}`} />
        <KPI label={tr("Boutique")} value={`€ ${report.revenue.orders_total.toLocaleString("fr")}`} sub={`${report.revenue.orders_paid} ${tr("payées")} · ${report.revenue.orders_pending} ${tr("en attente")}`} />
        <KPI label={tr("Cotisations payées")} value={`${report.membership.cotisation_paid} / ${report.membership.active}`} sub={`${report.membership.cotisation_due} ${tr("en attente")}`} accent="bg-club-yellow text-navy" />
      </div>

      {/* Bar chart by month */}
      <div className="bg-white border border-slate-200 p-6 mb-10">
        <div className="font-serif text-lg text-navy mb-4">{tr("Revenus par mois")} ({report.year})</div>
        <div className="flex items-end gap-2 h-48">
          {Object.entries(report.by_month).map(([m, v]) => (
            <div key={m} className="flex-1 flex flex-col items-center gap-1" title={`€ ${v.toFixed(2)}`}>
              <div className="w-full bg-club-red transition-all" style={{ height: `${Math.max(2, (v / maxMonth) * 160)}px` }} data-testid={`bar-${m}`} />
              <div className="text-[10px] uppercase tracking-widest text-slate-500">{tr(MONTHS_LBL[parseInt(m) - 1])}</div>
              <div className="text-[10px] text-navy font-medium">{v > 0 ? `€${v.toFixed(0)}` : ""}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Two columns: by category + by method (with donut charts) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
        <DonutBreakdown title={tr("Par catégorie")} data={report.by_category} testid="break-category" />
        <DonutBreakdown title={tr("Par mode de paiement")} data={report.by_method} testid="break-method" />
      </div>

      {/* Membership + Garages + Stages */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Section title={tr("Membres")}>
          <Row label="Total" value={report.membership.total} />
          <Row label={tr("Actifs")} value={report.membership.active} />
          <Row label={tr("En attente")} value={report.membership.pending} highlight={report.membership.pending > 0} />
          <Row label={tr("Cotisations dues")} value={report.membership.cotisation_due} highlight={report.membership.cotisation_due > 0} />
        </Section>
        <Section title={tr("Garages")}>
          <Row label="Total" value={report.garages.total} />
          <Row label={tr("Occupés")} value={report.garages.occupied} />
          <Row label={tr("Disponibles")} value={report.garages.available} highlight={report.garages.available > 0} />
        </Section>
        <Section title={tr("Stages")}>
          <Row label={tr("Inscriptions")} value={report.stages.total_registrations} />
          <Row label={tr("Confirmées")} value={report.stages.confirmed} />
        </Section>
      </div>
    </div>
  );
}

function KPI({ label, value, sub, accent }) {
  return (
    <div className={`p-5 ${accent || "bg-white border border-slate-200"}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70 mb-2">{label}</div>
      <div className="font-serif text-2xl md:text-3xl leading-tight">{value}</div>
      {sub && <div className="text-xs opacity-70 mt-1">{sub}</div>}
    </div>
  );
}

function BreakdownCard({ title, data, testid }) {
  const total = Object.values(data || {}).reduce((a, b) => a + b, 0) || 1;
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div className="bg-white border border-slate-200 p-5" data-testid={testid}>
      <div className="font-serif text-lg text-navy mb-3">{title}</div>
      {entries.length === 0 ? (
        <div className="text-sm text-slate-500">{tr("Aucune donnée.")}</div>
      ) : entries.map(([k, v]) => (
        <div key={k} className="mb-2">
          <div className="flex justify-between text-xs text-slate-600 mb-1">
            <span className="capitalize">{k.replace(/_/g, " ")}</span>
            <span className="font-medium text-navy">€ {v.toFixed(2)}</span>
          </div>
          <div className="h-2 bg-sand">
            <div className="h-full bg-club-red" style={{ width: `${(v / total) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

const DONUT_COLORS = ["#b8232f", "#0a1d3a", "#fdd247", "#1f6f8b", "#7b1e3a", "#3a8a55", "#c08019", "#5e4b8b"];

function DonutBreakdown({ title, data, testid }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((a, [, v]) => a + v, 0);
  if (!total) {
    return (
      <div className="bg-white border border-slate-200 p-5" data-testid={testid}>
        <div className="font-serif text-lg text-navy mb-3">{title}</div>
        <div className="text-sm text-slate-500">{tr("Aucune donnée.")}</div>
      </div>
    );
  }
  // Build SVG donut (circumference = 2πr with r=42 → ~263.89)
  const R = 42, C = 2 * Math.PI * R;
  let offset = 0;
  const segs = entries.map(([k, v], i) => {
    const frac = v / total;
    const dasharray = `${C * frac} ${C}`;
    const seg = { k, v, color: DONUT_COLORS[i % DONUT_COLORS.length], dasharray, offset: -offset };
    offset += C * frac;
    return seg;
  });
  return (
    <div className="bg-white border border-slate-200 p-5" data-testid={testid}>
      <div className="font-serif text-lg text-navy mb-3">{title}</div>
      <div className="flex items-center gap-5">
        <svg viewBox="0 0 100 100" className="w-32 h-32 shrink-0 -rotate-90" data-testid={`${testid}-donut`}>
          <circle cx="50" cy="50" r={R} fill="none" stroke="#f1eee5" strokeWidth="12" />
          {segs.map((s, i) => (
            <circle
              key={i} cx="50" cy="50" r={R} fill="none"
              stroke={s.color} strokeWidth="12"
              strokeDasharray={s.dasharray} strokeDashoffset={s.offset}
              strokeLinecap="butt"
            />
          ))}
          <text x="50" y="54" textAnchor="middle" transform="rotate(90 50 50)" className="fill-navy" style={{ fontSize: "11px", fontWeight: 700 }}>
            €{Math.round(total)}
          </text>
        </svg>
        <ul className="flex-1 space-y-1.5">
          {segs.map((s, i) => (
            <li key={i} className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-2 capitalize text-slate-700">
                <span className="inline-block w-2.5 h-2.5" style={{ backgroundColor: s.color }} />
                {s.k.replace(/_/g, " ")}
              </span>
              <span className="font-medium text-navy">€ {s.v.toFixed(2)} <span className="text-slate-400 ml-1">({Math.round((s.v / total) * 100)}%)</span></span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 p-5">
      <div className="font-serif text-lg text-navy mb-3">{title}</div>
      <ul>{children}</ul>
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <li className={`flex justify-between text-sm py-1.5 border-t border-slate-100 ${highlight ? "font-bold text-club-red" : "text-slate-700"}`}>
      <span>{label}</span>
      <span>{value}</span>
    </li>
  );
}


/* ============== INCOME / EXPENSES ENTRIES ============== */
function EntriesView({ entryType, year }) {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const isIncome = entryType === "income";
  const label = isIncome ? tr("Recettes") : tr("Dépenses");
  const accentBg = isIncome ? "bg-emerald-700 hover:bg-emerald-800" : "bg-rose-700 hover:bg-rose-800";
  const accentText = isIncome ? "text-emerald-700" : "text-rose-700";

  const load = async () => {
    try {
      const [{ data: rows }, { data: sum }] = await Promise.all([
        api.get("/treasury/entries", { params: { entry_type: entryType, year } }),
        api.get("/treasury/entries-summary", { params: { year } }),
      ]);
      setEntries(rows || []);
      setSummary(sum);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, [entryType, year]);

  const del = async (id) => { /* moved to EntryRow */ void id; void load; };
  void del;

  const filtered = entries.filter((e) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [e.category, e.party, e.description, e.reference, e.method]
      .some((v) => (v || "").toLowerCase().includes(s));
  });
  const total = filtered.reduce((a, b) => a + Number(b.amount || 0), 0);

  return (
    <div data-testid={`treasury-${entryType}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h3 className="font-serif text-2xl text-navy">{label} · {year}</h3>
          <p className="text-sm text-slate-500 mt-1">
            {isIncome
              ? tr("Recettes encaissées (cotisations, ventes, dons, sponsoring, autres revenus).") : tr("Dépenses engagées (achats, prestations, charges, salaires, autres frais).")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={tr("Rechercher (catégorie, tiers, description…)")}
            className="border border-slate-300 px-3 py-2 text-sm w-72"
            data-testid={`${entryType}-search`}
          />
          <button
            onClick={() => setShowForm((v) => !v)}
            className={`inline-flex items-center gap-2 ${accentBg} text-white px-4 py-2 text-xs uppercase tracking-[0.2em]`}
            data-testid={`${entryType}-add`}
          >{tr("+ Nouvelle écriture")}</button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
          <KPI label={tr("Total recettes")} value={`€ ${summary.income_total.toLocaleString("fr")}`} accent="bg-emerald-700 text-white" />
          <KPI label={tr("Total dépenses")} value={`€ ${summary.expense_total.toLocaleString("fr")}`} accent="bg-rose-700 text-white" />
          <KPI label={tr("Résultat net")} value={`€ ${summary.net.toLocaleString("fr")}`} accent={summary.net >= 0 ? "bg-navy text-white" : "bg-club-red text-white"} />
        </div>
      )}

      {showForm && (
        <EntryForm
          entryType={entryType}
          onClose={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); load(); }}
          busy={busy} setBusy={setBusy}
        />
      )}

      <div className="bg-white border border-slate-200 overflow-x-auto" data-testid={`${entryType}-table`}>
        <table className="w-full text-sm">
          <thead className="bg-navy text-white text-left">
            <tr>
              <th className="px-3 py-3 text-xs uppercase tracking-widest font-medium">{tr("Date")}</th>
              <th className="px-3 py-3 text-xs uppercase tracking-widest font-medium">{tr("Catégorie")}</th>
              <th className="px-3 py-3 text-xs uppercase tracking-widest font-medium">{isIncome ? "Payeur" : "Fournisseur"}</th>
              <th className="px-3 py-3 text-xs uppercase tracking-widest font-medium">{tr("Description")}</th>
              <th className="px-3 py-3 text-xs uppercase tracking-widest font-medium">Mode</th>
              <th className="px-3 py-3 text-xs uppercase tracking-widest font-medium text-right">{tr("Montant")}</th>
              <th className="px-3 py-3 text-xs uppercase tracking-widest font-medium">{tr("Preuve")}</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-12 text-center text-slate-500" data-testid={`${entryType}-empty`}>{tr("Aucune écriture pour")} {year}.</td></tr>
            ) : filtered.map((e) => (
              <EntryRow key={e.id} e={e} entryType={entryType} accentText={accentText} reload={load} />
            ))}
            {filtered.length > 0 && (
              <tr className="bg-sand border-t-2 border-navy">
                <td colSpan={5} className="px-3 py-3 text-right text-xs uppercase tracking-widest text-slate-600">{tr("Total filtré")}</td>
                <td className={`px-3 py-3 text-right font-serif text-lg ${accentText}`} data-testid={`${entryType}-total`}>€ {total.toFixed(2)}</td>
                <td colSpan={2}></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EntryRow({ e, entryType, accentText, reload }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({
    date: e.date || "", category: e.category || "", amount: e.amount || 0,
    party: e.party || "", description: e.description || "",
    method: e.method || "transfer", reference: e.reference || "", notes: e.notes || "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const del = async () => {
    if (!window.confirm(tr("Supprimer cette écriture (et la preuve associée) ?"))) return;
    await api.delete(`/treasury/entries/${e.id}`);
    reload();
  };
  const save = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
      if (file) fd.append("proof", file);
      await api.patch(`/treasury/entries/${e.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEdit(false); setFile(null);
      reload();
    } finally { setBusy(false); }
  };

  if (edit) {
    return (
      <tr className="border-t border-slate-100 bg-sand" data-testid={`${entryType}-edit-row-${e.id}`}>
        <td colSpan={8} className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <input type="date" value={form.date} onChange={(ev) => setForm({ ...form, date: ev.target.value })} className="border border-slate-300 px-2 py-1.5 text-sm" />
            <input type="number" step="0.01" value={form.amount} onChange={(ev) => setForm({ ...form, amount: ev.target.value })} className="border border-slate-300 px-2 py-1.5 text-sm" placeholder={tr("Montant")} />
            <input value={form.category} onChange={(ev) => setForm({ ...form, category: ev.target.value })} className="border border-slate-300 px-2 py-1.5 text-sm" placeholder={tr("Catégorie")} />
            <select value={form.method} onChange={(ev) => setForm({ ...form, method: ev.target.value })} className="border border-slate-300 px-2 py-1.5 text-sm">
              <option value="transfer">{tr("Virement")}</option><option value="cash">{tr("Espèces")}</option>
              <option value="card">{tr("Carte")}</option><option value="mollie">Mollie</option><option value="other">{tr("Autre")}</option>
            </select>
            <input value={form.party} onChange={(ev) => setForm({ ...form, party: ev.target.value })} className="border border-slate-300 px-2 py-1.5 text-sm md:col-span-2" placeholder="Tiers" />
            <input value={form.reference} onChange={(ev) => setForm({ ...form, reference: ev.target.value })} className="border border-slate-300 px-2 py-1.5 text-sm md:col-span-2" placeholder={tr("Référence")} />
            <input value={form.description} onChange={(ev) => setForm({ ...form, description: ev.target.value })} className="border border-slate-300 px-2 py-1.5 text-sm col-span-2 md:col-span-4" placeholder={tr("Description")} />
            <div className="col-span-2 md:col-span-4">
              <label className="text-[10px] uppercase tracking-widest text-slate-500">{tr("Remplacer la preuve")}</label>
              <input type="file" accept=".pdf,image/*" onChange={(ev) => setFile(ev.target.files[0])} className="block text-xs mt-1" />
              {e.proof_url && !file && <span className="text-xs text-slate-400">Actuelle : <a href={`${process.env.REACT_APP_BACKEND_URL}/api${e.proof_url}`} target="_blank" rel="noopener noreferrer" className="text-club-red underline">{e.proof_name || "preuve"}</a></span>}
            </div>
            <div className="col-span-2 md:col-span-4 flex gap-2">
              <button onClick={save} disabled={busy} className={`px-4 py-1.5 text-[10px] uppercase tracking-widest text-white ${entryType === "income" ? "bg-emerald-700 hover:bg-emerald-800" : "bg-rose-700 hover:bg-rose-800"} disabled:opacity-60`} data-testid={`${entryType}-edit-save-${e.id}`}>
                {busy ? "…" : tr("Enregistrer")}
              </button>
              <button onClick={() => setEdit(false)} className="border border-slate-300 px-4 py-1.5 text-[10px] uppercase tracking-widest hover:bg-slate-100">{tr("Annuler")}</button>
            </div>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid={`${entryType}-row-${e.id}`}>
      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{e.date}</td>
      <td className="px-3 py-2 text-navy">{e.category}</td>
      <td className="px-3 py-2 text-slate-700">{e.party || "—"}</td>
      <td className="px-3 py-2 text-slate-600 max-w-xs truncate" title={e.description}>{e.description || "—"}</td>
      <td className="px-3 py-2"><span className="text-[10px] uppercase tracking-widest bg-slate-100 px-2 py-0.5">{e.method}</span></td>
      <td className={`px-3 py-2 text-right font-semibold ${accentText}`}>€ {Number(e.amount).toFixed(2)}</td>
      <td className="px-3 py-2">
        {e.proof_url ? (
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api${e.proof_url}`} target="_blank" rel="noopener noreferrer" className="text-xs text-club-red underline" data-testid={`${entryType}-proof-${e.id}`}>{tr("Voir")}</a>
        ) : <span className="text-slate-400 text-xs">—</span>}
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        <button onClick={() => setEdit(true)} className="text-[10px] uppercase tracking-widest text-navy hover:underline mr-3" data-testid={`${entryType}-edit-${e.id}`}>{tr("Éditer")}</button>
        <button onClick={del} className="text-[10px] uppercase tracking-widest text-red-600 hover:underline" data-testid={`${entryType}-del-${e.id}`}>{tr("Supprimer")}</button>
      </td>
    </tr>
  );
}

function EntryForm({ entryType, onClose, onCreated, busy, setBusy }) {
  const isIncome = entryType === "income";
  const CATEGORIES = isIncome
    ? ["cotisation", "vente boutique", "stage", "événement", "régate", "don", "sponsoring", "subvention", "garage", "autre"]
    : ["fournisseur", "matériel", "entretien bateau", "ponton/hangar", "carburant", "salaire", "assurance", "loyer", "événement", "régate", "communication", "frais bancaire", "autre"];

  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    category: CATEGORIES[0],
    amount: "",
    party: "",
    description: "",
    method: "transfer",
    reference: "",
    notes: "",
  });
  const [file, setFile] = useState(null);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("entry_type", entryType);
      fd.append("date", form.date);
      fd.append("category", form.category);
      fd.append("amount", String(form.amount));
      fd.append("description", form.description);
      fd.append("party", form.party);
      fd.append("method", form.method);
      fd.append("reference", form.reference);
      fd.append("notes", form.notes);
      if (file) fd.append("proof", file);
      await api.post("/treasury/entries", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onCreated();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid={`${entryType}-form`}>
      <div className="md:col-span-2 flex items-center justify-between">
        <div className="label-eyebrow text-club-red">Nouvelle {isIncome ? "recette" : "dépense"}</div>
        <button type="button" onClick={onClose} className="text-slate-500 hover:text-navy" data-testid={`${entryType}-form-close`}><X className="w-4 h-4" /></button>
      </div>

      <Field2 label={tr("Date")} required>
        <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid={`${entryType}-date`} />
      </Field2>
      <Field2 label={tr("Montant (€)")} required>
        <input required type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid={`${entryType}-amount`} />
      </Field2>

      <Field2 label={tr("Catégorie")} required>
        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid={`${entryType}-category`}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field2>
      <Field2 label={tr("Mode de paiement")}>
        <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid={`${entryType}-method`}>
          <option value="transfer">{tr("Virement")}</option>
          <option value="cash">{tr("Espèces")}</option>
          <option value="card">{tr("Carte")}</option>
          <option value="mollie">Mollie</option>
          <option value="other">{tr("Autre")}</option>
        </select>
      </Field2>

      <Field2 label={isIncome ? tr("Payeur (nom)") : tr("Fournisseur (nom)")}>
        <input value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid={`${entryType}-party`} />
      </Field2>
      <Field2 label={tr("Référence / N° facture")}>
        <input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid={`${entryType}-reference`} />
      </Field2>

      <Field2 label={tr("Description")} className="md:col-span-2">
        <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid={`${entryType}-description`} />
      </Field2>

      <Field2 label="Notes internes" className="md:col-span-2">
        <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border border-slate-300 px-3 py-2" data-testid={`${entryType}-notes`} />
      </Field2>

      <Field2 label="Preuve (PDF, JPG, PNG, WEBP)" className="md:col-span-2">
        <input type="file" accept=".pdf,image/*" onChange={(e) => setFile(e.target.files[0])} className="text-sm" data-testid={`${entryType}-proof-input`} />
        {file && <div className="text-xs text-slate-500 mt-1">Sélectionné : {file.name}</div>}
      </Field2>

      {err && <div className="md:col-span-2 text-sm text-red-600" data-testid={`${entryType}-err`}>{err}</div>}

      <button type="submit" disabled={busy} className={`md:col-span-2 text-white py-3 text-xs uppercase tracking-[0.2em] disabled:opacity-60 ${isIncome ? "bg-emerald-700 hover:bg-emerald-800" : "bg-rose-700 hover:bg-rose-800"}`} data-testid={`${entryType}-submit`}>
        {busy ? tr("Enregistrement…") : `Enregistrer ${isIncome ? "la recette" : "la dépense"}`}
      </button>
    </form>
  );
}

function Field2({ label, children, required, className = "" }) {
  return (
    <div className={className}>
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">
        {label}{required && <span className="text-club-red ml-1">*</span>}
      </div>
      {children}
    </div>
  );
}

/* ============== GRAND LIVRE (consolidated chronological view) ============== */
function LedgerView({ year }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filterSource, setFilterSource] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const load = async () => {
    try {
      const params = { year };
      if (filterSource !== "all") params.source = filterSource;
      if (filterType !== "all") params.entry_type = filterType;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const { data } = await api.get("/treasury/ledger", { params });
      setData(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, [year, filterSource, filterType, fromDate, toDate]);

  const presetRange = (kind) => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (kind === "month") {
      setFromDate(iso(new Date(today.getFullYear(), today.getMonth(), 1)));
      setToDate(iso(today));
    } else if (kind === "quarter") {
      const qStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
      setFromDate(iso(qStart)); setToDate(iso(today));
    } else if (kind === "ytd") {
      setFromDate(`${today.getFullYear()}-01-01`); setToDate(iso(today));
    } else if (kind === "clear") {
      setFromDate(""); setToDate("");
    }
  };

  if (!data) return <div className="text-slate-500">{tr("Chargement…")}</div>;

  const filtered = data.entries.filter((r) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [r.category, r.party, r.description, r.reference, r.source_label]
      .some((v) => (v || "").toLowerCase().includes(s));
  });
  const SRC_BADGE = {
    manual:  "bg-amber-100 text-amber-800",
    payment: "bg-cyan-100 text-cyan-800",
    order:   "bg-fuchsia-100 text-fuchsia-800",
  };

  const exportCsv = () => {
    const header = ["Date", "Source", "Type", "Catégorie", "Tiers", "Description", "Mode", "Référence", "Montant"];
    const lines = [header.join(";")];
    filtered.forEach((r) => {
      lines.push([r.date, r.source_label, r.entry_type, r.category, r.party, r.description, r.method, r.reference, r.amount.toFixed(2)]
        .map((v) => `"${String(v || "").replace(/"/g, '""')}"`).join(";"));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `rycm-grand-livre-${year}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="treasury-ledger">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h3 className="font-serif text-2xl text-navy">{tr("Grand Livre")} · {data.year}</h3>
          <p className="text-sm text-slate-500 mt-1">{tr("Vue chronologique consolidée : écritures manuelles + cotisations payées + commandes boutique.")}</p>
        </div>
        <button onClick={exportCsv} className="inline-flex items-center gap-2 bg-navy text-white px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-club-red" data-testid="ledger-export">
          <Download className="w-3.5 h-3.5" />{tr("Export CSV")}</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KPI label={tr("Recettes")} value={`€ ${data.totals.income.toLocaleString("fr")}`} accent="bg-emerald-700 text-white" />
        <KPI label={tr("Dépenses")} value={`€ ${data.totals.expense.toLocaleString("fr")}`} accent="bg-rose-700 text-white" />
        <KPI label={tr("Résultat net")} value={`€ ${data.totals.net.toLocaleString("fr")}`} accent={data.totals.net >= 0 ? "bg-navy text-white" : "bg-club-red text-white"} />
        <KPI label={tr("Écritures")} value={data.totals.count} sub={tr("lignes")} />
      </div>

      <div className="flex flex-wrap gap-2 items-center mb-3">
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={tr("Rechercher (catégorie, tiers, référence…)")}
          className="flex-1 min-w-[220px] border border-slate-300 px-3 py-2 text-sm"
          data-testid="ledger-search"
        />
        <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)} className="border border-slate-300 px-3 py-2 text-sm" data-testid="ledger-source">
          <option value="all">{tr("Toutes sources")}</option>
          <option value="manual">Manuel</option>
          <option value="payment">Paiement</option>
          <option value="order">{tr("Boutique")}</option>
        </select>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="border border-slate-300 px-3 py-2 text-sm" data-testid="ledger-type">
          <option value="all">{tr("Recettes + Dépenses")}</option>
          <option value="income">{tr("Recettes")}</option>
          <option value="expense">{tr("Dépenses")}</option>
        </select>
      </div>

      <div className="flex flex-wrap gap-2 items-center mb-4 bg-sand border border-slate-200 px-3 py-2.5">
        <span className="text-[10px] uppercase tracking-widest text-slate-500">{tr("Période :")}</span>
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="border border-slate-300 px-2 py-1.5 text-sm" data-testid="ledger-from" />
        <span className="text-slate-400 text-xs">→</span>
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="border border-slate-300 px-2 py-1.5 text-sm" data-testid="ledger-to" />
        <button onClick={() => presetRange("month")} className="text-[10px] uppercase tracking-widest border border-navy text-navy px-2.5 py-1 hover:bg-navy hover:text-white" data-testid="ledger-preset-month">{tr("Ce mois")}</button>
        <button onClick={() => presetRange("quarter")} className="text-[10px] uppercase tracking-widest border border-navy text-navy px-2.5 py-1 hover:bg-navy hover:text-white" data-testid="ledger-preset-quarter">Trimestre</button>
        <button onClick={() => presetRange("ytd")} className="text-[10px] uppercase tracking-widest border border-navy text-navy px-2.5 py-1 hover:bg-navy hover:text-white" data-testid="ledger-preset-ytd">{tr("Année en cours")}</button>
        {(fromDate || toDate) && (
          <button onClick={() => presetRange("clear")} className="text-[10px] uppercase tracking-widest text-club-red hover:underline" data-testid="ledger-preset-clear">× Effacer</button>
        )}
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="ledger-table">
        <table className="w-full text-sm">
          <thead className="bg-navy text-white text-left">
            <tr>
              <th className="px-3 py-3 text-xs uppercase tracking-widest font-medium">{tr("Date")}</th>
              <th className="px-3 py-3 text-xs uppercase tracking-widest font-medium">Source</th>
              <th className="px-3 py-3 text-xs uppercase tracking-widest font-medium">{tr("Catégorie")}</th>
              <th className="px-3 py-3 text-xs uppercase tracking-widest font-medium">Tiers</th>
              <th className="px-3 py-3 text-xs uppercase tracking-widest font-medium">{tr("Description")}</th>
              <th className="px-3 py-3 text-xs uppercase tracking-widest font-medium">Mode</th>
              <th className="px-3 py-3 text-xs uppercase tracking-widest font-medium">{tr("Réf.")}</th>
              <th className="px-3 py-3 text-xs uppercase tracking-widest font-medium text-right">{tr("Montant")}</th>
              <th className="px-3 py-3 text-xs uppercase tracking-widest font-medium">{tr("Preuve")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={9} className="px-3 py-12 text-center text-slate-500" data-testid="ledger-empty">{tr("Aucune écriture pour")} {data.year}.</td></tr>
            ) : filtered.map((r) => (
              <tr key={`${r.source}-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`ledger-row-${r.source}-${r.id}`}>
                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{r.date}</td>
                <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${SRC_BADGE[r.source]}`}>{r.source_label}</span></td>
                <td className="px-3 py-2 text-navy capitalize">{r.category}</td>
                <td className="px-3 py-2 text-slate-700">{r.party || "—"}</td>
                <td className="px-3 py-2 text-slate-600 max-w-xs truncate" title={r.description}>{r.description || "—"}</td>
                <td className="px-3 py-2"><span className="text-[10px] uppercase tracking-widest bg-slate-100 px-2 py-0.5">{r.method || "—"}</span></td>
                <td className="px-3 py-2 text-slate-500 text-xs font-mono">{r.reference || "—"}</td>
                <td className={`px-3 py-2 text-right font-semibold ${r.entry_type === "income" ? "text-emerald-700" : "text-rose-700"}`}>
                  {r.entry_type === "expense" ? "− " : "+ "}€ {Number(r.amount).toFixed(2)}
                </td>
                <td className="px-3 py-2">
                  {r.proof_url ? (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api${r.proof_url}`} target="_blank" rel="noopener noreferrer" className="text-xs text-club-red underline">{tr("Voir")}</a>
                  ) : <span className="text-slate-400 text-xs">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

